import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { colors } from "../styles/colors";

import MunizaImg from "../assets/images/Muniza.png";
import GermanImg from "../assets/images/German.png";
import GeorginaImg from "../assets/images/Georgina.png";
import EhtishamImg from "../assets/images/Ehtisham.png";
import ShehreyarImg from "../assets/images/Shehreyar.png";
import Saleha from "../assets/images/Saleha.png";
import Fareha from "../assets/images/Fareha.png";
import Bilal from "../assets/images/Bilal.png";
import Danish from "../assets/images/Danish Khalil AM.png";


const TeamSection = styled.section`
  padding: 60px 20px 120px 20px; /* Reduced top padding from 120px to 60px to fix the large top space */
  text-align: center;
  background-color: ${colors.mutedGreen};
`;

const Subtitle = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.15rem;          /* Increased from 0.85rem to keep consistency with About/Services */
  font-weight: 700;            /* Made it bold */
  text-transform: uppercase;
  letter-spacing: 0.25em;      /* Elegant premium letter-spacing layout */
  color: ${colors.primary};
  display: block;
  margin-bottom: 15px;         /* Adjusted breathing room below it */
`;

const Heading = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  margin-bottom: 60px;
  color: ${colors.secondary};
`;

const LeaderRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
`;

const LeaderCard = styled(motion.div)`
  text-align: center;
  max-width: 320px;
  width: 100%;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const MemberCard = styled(motion.div)`
  text-align: left;
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  margin-bottom: 20px;
  position: relative;
  border-bottom: 3px solid ${colors.primary};
`;

const MemberImage = styled(motion.img)`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
`;

const AvatarPlaceholder = styled(motion.div)`
  width: 100%;
  aspect-ratio: 1 / 1;
  background-color: #e8ece9; 
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary || '#777777'};
`;

const MemberName = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  color: ${colors.secondary};
  margin: 0 0 5px 0;
  font-weight: 600;
`;

const MemberRole = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #777777;
  margin: 0;
`;

export default function Team() {
  const leader = { name: "Saleha Safdar", role: "CEO & Founder", image: Saleha };

  const advisoryTeam = [
    { name: "Shehreyar Khan", role: "Business and Marketing Advisor", image: ShehreyarImg },
    { name: "Muniza Ashraf", role: "Sales Manager", image: MunizaImg },
    { name: "Fareha Safdar", role: "Property Management Advisor", image: Fareha },
    { name: "Georgina Griffith", role: "Real Estate Advisor", image: GeorginaImg },
    { name: "Ehtisham Khan", role: "Medical Billing Advisor", image: EhtishamImg },
    { name: "Bilal Ashraf", role: "Renovation and Fitouts Advisor", image: Bilal },
    { name: "Danish Khalil", role: "E-commerce services", image: Danish }, 
    { name: "German Elizarov", role: "Business Consultancy", image: GermanImg },
    { name: "Muhammad Sabih", role: "Mortgage and loan Advisor", image: null }, 
  ];

  const animProps = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <TeamSection id="team">
      <Subtitle>Integrity & Growth</Subtitle>
      <Heading>Meet Our Executive Advisory Team</Heading>
      
      <LeaderRow>
        <LeaderCard {...animProps}>
          <ImageWrapper>
            <MemberImage 
              src={leader.image} 
              alt={leader.name}
              whileHover={{ scale: 1.04 }}
              transition={{ duration: 0.4 }}
            />
          </ImageWrapper>
          <MemberName style={{ textAlign: 'center' }}>{leader.name}</MemberName>
          <MemberRole style={{ textAlign: 'center' }}>{leader.role}</MemberRole>
        </LeaderCard>
      </LeaderRow>

      <TeamGrid>
        {advisoryTeam.map((member, i) => (
          <MemberCard
            key={i}
            {...animProps}
            transition={{ ...animProps.transition, delay: i * 0.08 }}
          >
            <ImageWrapper>
              {member.image ? (
                <MemberImage 
                  src={member.image} 
                  alt={member.name}
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.4 }}
                />
              ) : (
                <AvatarPlaceholder
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.4 }}
                >
                  <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </AvatarPlaceholder>
              )}
            </ImageWrapper>
            <MemberName>{member.name}</MemberName>
            <MemberRole>{member.role}</MemberRole>
          </MemberCard>
        ))}
      </TeamGrid>
    </TeamSection>
  );
}